import type { ChatActionTool } from './chat-actions'

/** 工具执行后的统一生命周期。 */
export type ToolLifecycle = 'query' | 'action' | 'terminal'

/** 参数拒绝发生在副作用之前，允许模型修正一次；执行失败不能按此重试。 */
export class ToolInputError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ToolInputError'
  }
}

/** 普通聊天动作：执行后把简短结果回送给模型，允许继续编排。 */
export const ACTION_TOOL_NAMES: ReadonlySet<string> = new Set([
  'send_images',
  'send_sticker',
  'pat',
  'group_chat_response',
  'create_memory',
  'create_post',
  'like_post',
  'comment_on_post',
  'set_reminder',
  'schedule_task',
  'plugin_action',
  'update_relationship',
])

/** 执行后必须立刻结束本轮的强终止动作。 */
export const TERMINAL_TOOL_NAMES: ReadonlySet<string> = new Set([
  'send_messages',
  'silent',
  'recall_message',
  'initiate_voice_call',
  'initiate_video_call',
  'hang_up_call',
  'speak_in_call',
  'answer_call',
  'block_user',
  'friend_request_response',
  'respond_to_unblock_opportunity',
  'submit_conversation_summary',
  // 朋友圈后台模拟动作使用独立循环，但保留终止语义。
  'create_moments_post',
  'like_moments_post',
  'comment_on_moments_post',
  'do_nothing_moments',
])

export interface ToolLifecycleConfig {
  function: { name: string }
  lifecycle?: ToolLifecycle
  isTerminal?: boolean
}

/**
 * 从新生命周期字段和旧 isTerminal 字段推导行为。
 * 对旧工具采用名称白名单优先，确保已有插件的接口不需要立刻迁移。
 */
export function resolveToolLifecycle(tool: ToolLifecycleConfig, legacyNames?: ReadonlySet<string>): ToolLifecycle {
  const name = tool.function.name
  if (tool.lifecycle)
    return tool.lifecycle
  if (legacyNames?.has(name) || TERMINAL_TOOL_NAMES.has(name))
    return 'terminal'
  if (ACTION_TOOL_NAMES.has(name))
    return 'action'
  return tool.isTerminal ? 'terminal' : 'query'
}

export function getToolLifecycle(tool: Pick<ChatActionTool, 'function' | 'isTerminal' | 'lifecycle'>): ToolLifecycle {
  return resolveToolLifecycle(tool)
}

export function getToolLifecycleByName(name: string, lifecycleByName?: ReadonlyMap<string, ToolLifecycle>, legacyNames?: ReadonlySet<string>): ToolLifecycle {
  return resolveToolLifecycle({
    function: { name },
    lifecycle: lifecycleByName?.get(name),
  }, legacyNames)
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value))
    return value.map(stableValue)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, stableValue(item)]))
  }
  return value
}

export function stableToolArguments(value: unknown): string {
  try {
    return JSON.stringify(stableValue(value ?? {}))
  }
  catch {
    return String(value)
  }
}

export interface ToolClaim {
  accepted: boolean
  reason?: 'duplicate_id' | 'duplicate_arguments' | 'budget'
}

/** 单轮动作账本：同时按 toolCallId 和工具+参数签名去重，并限制总调用数。 */
export class ToolExecutionLedger {
  private readonly ids = new Set<string>()
  private readonly signatures = new Set<string>()
  private _count = 0
  private budgetNotified = false
  private inputCorrections = 0

  constructor(public readonly maxCalls = 20) {}

  get count(): number {
    return this._count
  }

  /** 当前调用是否已经消耗了最后一个预算名额。 */
  get budgetReached(): boolean {
    return this._count === this.maxCalls
  }

  claim(toolCallId: string | undefined, toolName: string, input: unknown): ToolClaim {
    if (toolCallId && this.ids.has(toolCallId))
      return { accepted: false, reason: 'duplicate_id' }

    const signature = `${toolName}:${stableToolArguments(input)}`
    if (this.signatures.has(signature))
      return { accepted: false, reason: 'duplicate_arguments' }

    if (this._count >= this.maxCalls)
      return { accepted: false, reason: 'budget' }

    if (toolCallId)
      this.ids.add(toolCallId)
    this.signatures.add(signature)
    this._count++
    return { accepted: true }
  }

  notifyBudgetOnce(callback?: () => void): void {
    if (this.budgetNotified)
      return
    this.budgetNotified = true
    callback?.()
  }

  allowInputCorrection(error: unknown, toolCallId: string, toolName: string, input: unknown): boolean {
    if (!(error instanceof ToolInputError) || this.inputCorrections >= 1)
      return false
    this.inputCorrections++
    // 被拒绝的输入未产生副作用，不消耗工具预算，允许模型在下一轮修正。
    this.ids.delete(toolCallId)
    this.signatures.delete(`${toolName}:${stableToolArguments(input)}`)
    this._count = Math.max(0, this._count - 1)
    return true
  }
}

export function duplicateToolResult(claim: ToolClaim): string {
  if (claim.reason === 'budget')
    return '本轮工具调用已达到上限，已停止继续执行。'
  if (claim.reason === 'duplicate_id')
    return '已跳过重复的工具调用。'
  return '已跳过相同工具和参数的重复调用。'
}

export function ignoredAfterTerminalResult(): string {
  return '本批调用已执行终止动作，后续工具调用已忽略。'
}

/** 所有工具仍可返回字符串；空成功结果统一补成模型可理解的短确认。 */
export function normalizeToolResult(result: unknown, toolName: string, lifecycle: ToolLifecycle): string {
  if (typeof result === 'string' && result.trim())
    return result
  if (result != null && typeof result !== 'string') {
    try {
      return JSON.stringify(result)
    }
    catch {
      return String(result)
    }
  }
  return lifecycle === 'action' ? `已执行 ${toolName}。` : ''
}
